import React, { useEffect, useMemo, useState } from 'react';
import { Button, Collapse, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import './GroupedCandidateRegimenTable.css';

const { Text } = Typography;

export const CANDIDATE_SOURCE_INDEX = '__candidateSourceIndex';

interface CandidateEvaluationBadge {
  label: string;
  color?: string;
}

interface IndexedCandidateRegimen extends Record<string, any> {
  [CANDIDATE_SOURCE_INDEX]: number;
  key: string;
}

export interface CandidateIntervalGroup {
  key: string;
  interval: number | null;
  label: string;
  rows: IndexedCandidateRegimen[];
}

interface GroupedCandidateRegimenTableProps {
  rows: Record<string, any>[];
  columns: ColumnsType<any>;
  selectedIndex: number | null;
  onSelect: (sourceIndex: number) => void;
  getEvaluation: (row: Record<string, any>, sourceIndex: number) => CandidateEvaluationBadge;
  isRecommended: (row: Record<string, any>, sourceIndex: number) => boolean;
  formatDose: (dose: number) => string;
  className?: string;
  isContinuous?: boolean;
  isMobile?: boolean;
  renderMobileRow?: (row: Record<string, any>, sourceIndex: number) => React.ReactNode;
  resetKey?: React.Key | null;
}

const numericInterval = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const buildCandidateIntervalGroups = (
  rows: Record<string, any>[],
  isContinuous = false,
): CandidateIntervalGroup[] => {
  const groups = new Map<string, CandidateIntervalGroup>();

  rows.forEach((row, sourceIndex) => {
    const interval = numericInterval(row.interval ?? row.interval_h);
    const key = isContinuous ? 'continuous' : interval == null ? 'unmarked' : `interval-${interval}`;
    const label = isContinuous ? '连续输注' : interval == null ? '间隔未标记' : `Q${interval}h`;
    const indexedRow: IndexedCandidateRegimen = {
      ...row,
      [CANDIDATE_SOURCE_INDEX]: sourceIndex,
      key: `candidate-${sourceIndex}`,
    };
    const existing = groups.get(key);
    if (existing) {
      existing.rows.push(indexedRow);
    } else {
      groups.set(key, { key, interval, label, rows: [indexedRow] });
    }
  });

  return Array.from(groups.values()).sort((left, right) => {
    if (left.key === 'continuous') return -1;
    if (right.key === 'continuous') return 1;
    if (left.interval == null) return 1;
    if (right.interval == null) return -1;
    return right.interval - left.interval;
  });
};

export const resolveCandidateSourceIndex = (
  row: Record<string, any>,
  fallbackIndex: number,
): number => {
  const sourceIndex = row?.[CANDIDATE_SOURCE_INDEX];
  return Number.isInteger(sourceIndex) ? sourceIndex : fallbackIndex;
};

const uniqueKeys = (keys: Array<string | null | undefined>): string[] => (
  Array.from(new Set(keys.filter((key): key is string => !!key)))
);

export const getDefaultCandidateGroupKeys = (
  groups: CandidateIntervalGroup[],
  selectedIndex: number | null,
  _isRecommended: (row: Record<string, any>, sourceIndex: number) => boolean,
  _isMobile = false,
): string[] => {
  // Candidate drawers are intentionally collapsed until the clinician chooses
  // a regimen.  Selection (or an explicit user click) is what discloses a
  // group; do not implicitly open the recommended/first interval on load.
  if (selectedIndex == null) return [];

  const selectedGroup = selectedIndex == null
    ? null
    : groups.find((group) => group.rows.some(
      (row) => row[CANDIDATE_SOURCE_INDEX] === selectedIndex,
    ))?.key;
  return uniqueKeys([selectedGroup]).slice(0, 1);
};

const preferredEvaluationOrder = ['符合目标', '需临床复核', '未满足条件'];

const evaluationOrder = (label: string): number => {
  const index = preferredEvaluationOrder.indexOf(label);
  return index >= 0 ? index : preferredEvaluationOrder.length;
};

export const formatCandidateEvaluationCount = (label: string, count: number): string => {
  return `${label} ×${count}`;
};

const GroupedCandidateRegimenTable: React.FC<GroupedCandidateRegimenTableProps> = ({
  rows,
  columns,
  selectedIndex,
  onSelect,
  getEvaluation,
  isRecommended,
  formatDose,
  className,
  isContinuous = false,
  isMobile = false,
  renderMobileRow,
  resetKey,
}) => {
  const groups = useMemo(
    () => buildCandidateIntervalGroups(rows, isContinuous),
    [isContinuous, rows],
  );
  const groupSignature = useMemo(
    () => groups.map((group) => `${group.key}:${group.rows.map(
      (row) => row[CANDIDATE_SOURCE_INDEX],
    ).join(',')}`).join('|'),
    [groups],
  );
  const defaultActiveKeys = useMemo(
    () => getDefaultCandidateGroupKeys(groups, selectedIndex, isRecommended, isMobile),
    [groups, isMobile, isRecommended, selectedIndex],
  );
  const [activeKeys, setActiveKeys] = useState<string[]>(defaultActiveKeys);

  useEffect(() => {
    setActiveKeys(defaultActiveKeys);
    // A new calculation must reset disclosure state even when the interval grid is unchanged.
  }, [groupSignature, isMobile, resetKey]);

  useEffect(() => {
    if (selectedIndex == null) return;
    const selectedGroupKey = groups.find((group) => group.rows.some(
      (row) => row[CANDIDATE_SOURCE_INDEX] === selectedIndex,
    ))?.key;
    if (!selectedGroupKey) return;
    setActiveKeys((current) => {
      if (current.includes(selectedGroupKey)) return current;
      return [selectedGroupKey];
    });
  }, [groups, selectedIndex]);

  const groupItems = groups.map((group) => {
    const sourceIndices = group.rows.map((row) => row[CANDIDATE_SOURCE_INDEX]);
    const containsSelection = selectedIndex != null && sourceIndices.includes(selectedIndex);
    const evaluationCounts = new Map<string, { count: number; color?: string }>();
    group.rows.forEach((row) => {
      const sourceIndex = row[CANDIDATE_SOURCE_INDEX];
      const evaluation = getEvaluation(row, sourceIndex);
      const current = evaluationCounts.get(evaluation.label);
      evaluationCounts.set(evaluation.label, {
        count: (current?.count ?? 0) + 1,
        color: current?.color ?? evaluation.color,
      });
    });
    const doses = group.rows
      .map((row) => Number(row.dose ?? row.dose_mg))
      .filter((dose) => Number.isFinite(dose));
    const minDose = doses.length > 0 ? Math.min(...doses) : null;
    const maxDose = doses.length > 0 ? Math.max(...doses) : null;
    const doseRange = minDose == null || maxDose == null
      ? null
      : minDose === maxDose
        ? formatDose(minDose)
        : `${formatDose(minDose)} – ${formatDose(maxDose)}`;

    const label = (
      <div className="candidate-regimen-group__header">
        <Space size={6} wrap>
          <Text strong className="candidate-regimen-group__interval">{group.label}</Text>
          <Text type="secondary">{group.rows.length} 个方案</Text>
          {doseRange && <Text type="secondary">剂量 {doseRange}</Text>}
        </Space>
        <Space size={4} wrap className="candidate-regimen-group__status-list">
          {Array.from(evaluationCounts.entries())
            .sort(([left], [right]) => evaluationOrder(left) - evaluationOrder(right))
            .map(([labelText, value]) => (
              <Tag key={labelText} color={value.color} className="candidate-regimen-group__count-tag">
                {formatCandidateEvaluationCount(labelText, value.count)}
              </Tag>
            ))}
          {containsSelection && <Tag color="blue">当前选中</Tag>}
        </Space>
      </div>
    );

    return {
      key: group.key,
      label,
      children: isMobile ? (
        <div className="candidate-regimen-group__mobile-list" role="list">
          {renderMobileRow ? (
            group.rows.map((row) => (
              <div key={row.key} className="candidate-regimen-group__mobile-item" role="listitem">
                {renderMobileRow(row, row[CANDIDATE_SOURCE_INDEX])}
              </div>
            ))
          ) : (
            <Text type="secondary" className="candidate-regimen-group__mobile-unavailable">
              移动端候选方案暂不可用
            </Text>
          )}
        </div>
      ) : (
        <Table
          className={className}
          dataSource={group.rows}
          columns={columns}
          pagination={false}
          size="small"
          tableLayout="fixed"
          rowClassName={(row) => (
            selectedIndex === row[CANDIDATE_SOURCE_INDEX] ? 'ant-table-row-selected' : ''
          )}
          onRow={(row) => ({
            onClick: () => onSelect(row[CANDIDATE_SOURCE_INDEX]),
          })}
        />
      ),
    };
  });

  return (
    <div
      className={`candidate-regimen-groups candidate-regimen-groups--${isMobile ? 'mobile' : 'desktop'}`}
      data-layout={isMobile ? 'mobile-cards' : 'desktop-table'}
    >
      <div className="candidate-regimen-groups__toolbar">
        <Text type="secondary">共 {rows.length} 个候选方案，按给药间隔分组</Text>
        <Space size={4}>
          <Button type="link" size="small" onClick={() => setActiveKeys([])}>
            全部收起
          </Button>
        </Space>
      </div>
      <Collapse
        className="candidate-regimen-groups__collapse"
        activeKey={activeKeys}
        accordion
        onChange={(keys) => setActiveKeys(
          Array.isArray(keys) ? keys.map(String) : keys ? [String(keys)] : [],
        )}
        items={groupItems}
      />
    </div>
  );
};

export default GroupedCandidateRegimenTable;
